import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ResolveMatchRequest } from '@shop/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import type { MatchQueueItem } from '../lib/types';
import { formatMoney } from '../lib/utils';

const queueQuery = {
  queryKey: ['suppliers', 'match-queue'],
  queryFn: () => api<{ items: MatchQueueItem[] }>('/suppliers/match-queue'),
};

export function MatchQueuePage() {
  const { t } = useTranslation();
  const queue = useQuery(queueQuery);

  return (
    <>
      <PageHeader title={t('match.title')} description={t('match.hint')} />
      {queue.error ? <Alert variant="destructive">{errorMessage(queue.error)}</Alert> : null}
      {queue.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('common.empty')}</p>
      ) : null}
      <div className="grid gap-4">
        {(queue.data?.items ?? []).map((item) => (
          <MatchCard key={item.queue.id} item={item} />
        ))}
      </div>
    </>
  );
}

function MatchCard({ item }: { item: MatchQueueItem }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [variantId, setVariantId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const { offer } = item;

  const resolve = async (body: ResolveMatchRequest) => {
    setBusy(true);
    setError(undefined);
    try {
      await api(`/suppliers/match-queue/${item.queue.id}/resolve`, { method: 'POST', body });
      await queryClient.invalidateQueries({ queryKey: queueQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ['catalog', 'products'] });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="grid gap-4 p-5 lg:grid-cols-[1fr_1fr]">
        <div className="grid content-start gap-1 text-sm">
          <p className="text-xs uppercase text-muted-foreground">
            {t('match.offer')} · {item.supplierName}
          </p>
          <p className="font-medium">{offer.title ?? offer.supplierSku}</p>
          <p className="text-muted-foreground">
            SKU {offer.supplierSku}
            {offer.gtin ? ` · GTIN ${offer.gtin}` : ''}
            {offer.mpn ? ` · MPN ${offer.mpn}` : ''}
            {offer.brandName ? ` · ${offer.brandName}` : ''}
          </p>
          <p>
            {formatMoney(offer.costMinor, offer.currency, i18n.language)} · {t('suppliers.stock')}:{' '}
            {offer.stockQty}
          </p>
        </div>
        <div className="grid content-start gap-2">
          <p className="text-xs uppercase text-muted-foreground">{t('match.candidates')}</p>
          {item.queue.candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('match.noCandidates')}</p>
          ) : (
            <ul className="divide-y rounded-md border text-sm">
              {item.queue.candidates.map((c) => (
                <li key={c.variantId} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span>
                    <span className="font-medium">{c.name ?? c.sku}</span>{' '}
                    <span className="text-xs text-muted-foreground">
                      {c.sku} · {t('match.score', { score: Math.round(c.score * 100) })}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void resolve({ action: 'link', variantId: c.variantId })}
                  >
                    {t('match.link')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <Input
              aria-label={t('match.linkById')}
              placeholder={t('match.linkById')}
              value={variantId}
              onChange={(e) => {
                setVariantId(e.target.value.trim());
              }}
            />
            <Button
              variant="outline"
              disabled={busy || !variantId}
              onClick={() => void resolve({ action: 'link', variantId })}
            >
              {t('match.link')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => void resolve({ action: 'create_product' })}>
              {t('match.createProduct')}
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => void resolve({ action: 'reject' })}
            >
              {t('match.reject')}
            </Button>
          </div>
          {error ? <Alert variant="destructive">{error}</Alert> : null}
        </div>
      </CardContent>
    </Card>
  );
}
